/*
 * AuthCallback — handles the Emergent Google SSO redirect.
 *
 * Emergent redirects the user back to `{origin}/admin/sso/callback#session_id=...`.
 * We grab the session_id from the URL fragment, POST it to the backend,
 * then redirect to /admin (the admin home).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH (the redirect target is computed dynamically from
 * window.location.origin elsewhere).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("No session_id in URL fragment — auth may have failed upstream.");
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const r = await api.post("/auth/sso/exchange", { session_id: sessionId });
        const data = r.data;
        if (data?.token) {
          localStorage.setItem("token", data.token);
        }
        setUser?.({ ...data.user, token: data.token });
        toast.success(`Welcome ${data.user?.name || data.user?.email}`);
        // Clear the hash so a refresh doesn't re-trigger the exchange.
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true, state: { user: data.user } });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = detail?.message || detail || "SSO exchange failed.";
        setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-[min(100%,420px)] rounded-2xl border border-border bg-card p-6 text-center" data-testid="auth-callback">
        {error ? (
          <>
            <ShieldAlert className="w-8 h-8 mx-auto text-rose-700 mb-2" />
            <h1 className="display text-xl mb-1">Sign-in failed</h1>
            <p className="text-[13px] text-foreground/60 mb-4">{error}</p>
            <a href="/admin/login" className="inline-block px-4 py-2 rounded border text-[13px] hover:bg-foreground/5">
              Back to admin sign-in
            </a>
          </>
        ) : (
          <>
            <Loader2 className="w-7 h-7 mx-auto animate-spin text-foreground/60 mb-2" />
            <h1 className="display text-xl mb-1">Verifying your session</h1>
            <p className="text-[12.5px] text-foreground/55">Hold tight…</p>
          </>
        )}
      </div>
    </div>
  );
}
